import {gql} from '@apollo/client';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  createStyles,
  Divider,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  SkippableBenchlingQueryResult,
  useBenchlingQuery,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {aiSettingsContentInlinePadding} from 'llm/chat-library/ui/AIStyleConstants';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';
import {assertNever} from 'util/Types';

import {
  RoutineMemorySetVersionQuery,
  RoutineMemorySetVersionQueryVariables,
} from './__generated/RoutineMemorySetVersionQuery';
import {
  RoutineMemorySetVersionsQuery,
  RoutineMemorySetVersionsQueryVariables,
} from './__generated/RoutineMemorySetVersionsQuery';

export const ROUTINE_MEMORY_SET_VERSIONS_QUERY = gql`
  query RoutineMemorySetVersionsQuery($routineId: StrictString!) @engComponent(name: LLM) @skipAudit {
    routineMemorySetVersions(routineId: $routineId) {
      versions {
        versionId
        createdAt
        members {
          path
          versionNumber
        }
      }
      totalCount
      truncated
    }
  }
`;

export const ROUTINE_MEMORY_SET_VERSION_QUERY = gql`
  query RoutineMemorySetVersionQuery($routineId: StrictString!, $versionId: StrictString!)
  @engComponent(name: LLM)
  @skipAudit {
    routineMemorySetVersion(routineId: $routineId, versionId: $versionId) {
      versionId
      createdAt
      pages {
        page
        content
      }
    }
  }
`;

interface QueryingMemoryViewerPanelProps {
  agentName: string;
  onClose: () => void;
  routineId: string;
  // Preselect a specific set version/file when the viewer is opened via a deep link. If the set version is
  // outside the loaded history list it is still fetched by id; the file preselects once it loads.
  initialVersionId?: string | null;
  initialPage?: string | null;
}

type SetVersion = RoutineMemorySetVersionsQuery['routineMemorySetVersions']['versions'][number];
type MemoryPage = NonNullable<RoutineMemorySetVersionQuery['routineMemorySetVersion']>['pages'][number];

interface MemoryViewerPanelProps {
  agentName: string;
  initialPage: string | null;
  initialVersionId: string | null;
  onClose: () => void;
  routineId: string;
  totalSetVersionCount: number;
  versions: Array<SetVersion>;
  versionsTruncated: boolean;
}

const MEMORY_VIEWER_PANEL_TITLE_ID = 'agent-memory-viewer-title';

interface SetVersionMember {
  path: string;
  versionNumber: number;
}

function formatChangedPaths(verb: string, paths: Array<string>): string {
  return paths.length === 1 ? `${verb} ${paths[0]}` : `${verb} ${paths.length} files: ${paths.join(', ')}`;
}

function summarizeSetVersionChanges(
  members: Array<SetVersionMember>,
  predecessorMembers: Array<SetVersionMember> | null
): string {
  if (predecessorMembers == null) {
    return 'Changes unavailable';
  }
  const currentVersionByPath = new Map(members.map((member) => [member.path, member.versionNumber]));
  const predecessorVersionByPath = new Map(
    predecessorMembers.map((member) => [member.path, member.versionNumber])
  );
  const addedPaths = members
    .filter((member) => !predecessorVersionByPath.has(member.path))
    .map((member) => member.path);
  const updatedPaths = members
    .filter(
      (member) =>
        predecessorVersionByPath.has(member.path) &&
        predecessorVersionByPath.get(member.path) !== member.versionNumber
    )
    .map((member) => member.path);
  const archivedPaths = predecessorMembers
    .filter((member) => !currentVersionByPath.has(member.path))
    .map((member) => member.path);
  const summaries = [
    ...(addedPaths.length > 0 ? [formatChangedPaths('Added', addedPaths)] : []),
    ...(updatedPaths.length > 0 ? [formatChangedPaths('Updated', updatedPaths)] : []),
    ...(archivedPaths.length > 0 ? [formatChangedPaths('Archived', archivedPaths)] : []),
  ];
  return summaries.length > 0 ? summaries.join(' · ') : 'No file changes';
}

const useQueryStateStyles = createStyles((theme) => ({
  content: {
    flex: '1 1 auto',
  },
  header: {
    flexShrink: 0,
    padding: theme.spacing.md,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
}));

const useStyles = createStyles((theme) => ({
  body: {
    display: 'flex',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  content: {
    boxSizing: 'border-box',
    flex: '1 1 auto',
    width: '100%',
    minWidth: 0,
    overflow: 'auto',
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.lg,
    paddingLeft: aiSettingsContentInlinePadding(theme.spacing.lg),
    paddingRight: aiSettingsContentInlinePadding(theme.spacing.lg),
  },
  contentPath: {
    overflowWrap: 'anywhere',
  },
  header: {
    flexShrink: 0,
    padding: theme.spacing.md,
  },
  pageButton: {
    fontWeight: 400,
    height: 'auto',
    overflowWrap: 'anywhere',
    paddingBottom: theme.spacing.xs,
    paddingTop: theme.spacing.xs,
    textAlign: 'left',
    width: '100%',
  },
  pageButtonInner: {
    justifyContent: 'flex-start',
  },
  pageButtonLabel: {
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
  },
  pageList: {
    borderRight: `1px solid ${theme.colors.gray[3]}`,
    flex: '0 0 240px',
    overflowY: 'auto',
    padding: theme.spacing.md,
  },
  pageListControls: {
    backgroundColor: theme.white,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  setVersionMenuDropdown: {
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  setVersionMenuItem: {
    paddingBottom: theme.spacing.sm,
    paddingTop: theme.spacing.sm,
  },
  setVersionMenuItemSelected: {
    backgroundColor: theme.colors.blue[0],
  },
  setVersionMenuSummary: {
    overflowWrap: 'anywhere',
  },
  setVersionMenuNote: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
  },
}));

function MemoryViewerQueryState({
  agentName,
  children,
  onClose,
  role,
}: {
  agentName: string;
  children: React.ReactNode;
  onClose: () => void;
  role?: 'alert' | 'status';
}): JSX.Element {
  const {classes} = useQueryStateStyles();
  return (
    <div aria-labelledby={MEMORY_VIEWER_PANEL_TITLE_ID} className={classes.panel} role="region">
      <Stack className={classes.header} spacing="md">
        <Group spacing="xs">
          <ActionIcon
            aria-label={`Back to ${agentName}`}
            autoFocus={true}
            onClick={onClose}
            size="sm"
            variant="ghost"
          >
            <BenchlingIcon icon="arrow-left" />
          </ActionIcon>
          <Text size="lg" fw={600}>
            {agentName}
          </Text>
        </Group>
        <Text component="h4" fw={600} id={MEMORY_VIEWER_PANEL_TITLE_ID} size="xl">
          Memory
        </Text>
      </Stack>
      <Divider />
      <Center className={classes.content} role={role}>
        {children}
      </Center>
    </div>
  );
}

export default function QueryingMemoryViewerPanel({
  agentName,
  onClose,
  routineId,
  initialVersionId = null,
  initialPage = null,
}: QueryingMemoryViewerPanelProps): JSX.Element {
  const versionsResult = useBenchlingQuery<
    RoutineMemorySetVersionsQuery,
    RoutineMemorySetVersionsQueryVariables
  >(ROUTINE_MEMORY_SET_VERSIONS_QUERY, {
    variables: {routineId},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });

  if (versionsResult.type === QueryResultType.PENDING) {
    return (
      <MemoryViewerQueryState agentName={agentName} onClose={onClose} role="status">
        <Stack align="center" spacing="xs">
          <Loader size="sm" />
          <Text size="sm" color="dimmed">
            Loading memory...
          </Text>
        </Stack>
      </MemoryViewerQueryState>
    );
  }
  if (versionsResult.type === QueryResultType.ERROR) {
    return (
      <MemoryViewerQueryState agentName={agentName} onClose={onClose} role="alert">
        <Text size="sm">Unable to load this agent's memory.</Text>
      </MemoryViewerQueryState>
    );
  }

  const {totalCount, truncated, versions} = versionsResult.data.routineMemorySetVersions;
  if (versions.length === 0) {
    return (
      <MemoryViewerQueryState agentName={agentName} onClose={onClose}>
        <Text size="sm" color="dimmed">
          This agent has no memory yet.
        </Text>
      </MemoryViewerQueryState>
    );
  }
  return (
    <MemoryViewerPanel
      agentName={agentName}
      initialPage={initialPage}
      initialVersionId={initialVersionId}
      onClose={onClose}
      routineId={routineId}
      totalSetVersionCount={totalCount}
      versions={versions}
      versionsTruncated={truncated}
    />
  );
}

type MemoryContentState =
  | {type: 'loading'}
  | {type: 'error'}
  | {type: 'unavailable'}
  | {type: 'empty-set-version'}
  | {type: 'page'; memoryPage: MemoryPage};

function getMemoryContentState(
  setVersionResult: SkippableBenchlingQueryResult<
    RoutineMemorySetVersionQuery,
    RoutineMemorySetVersionQueryVariables
  >,
  effectiveVersionId: string | null,
  selectedPage: string | null
): MemoryContentState {
  switch (setVersionResult.type) {
    case QueryResultType.PENDING:
      return {type: 'loading'};
    case QueryResultType.ERROR:
      return {type: 'error'};
    case QueryResultType.SKIPPED:
      return {type: 'unavailable'};
    case QueryResultType.SUCCESS: {
      const setVersion = setVersionResult.data.routineMemorySetVersion;
      if (setVersion != null && setVersion.versionId !== effectiveVersionId) {
        return {type: 'loading'};
      }
      if (setVersion == null) {
        return setVersionResult.loading ? {type: 'loading'} : {type: 'unavailable'};
      }
      const [firstPage] = setVersion.pages;
      if (firstPage == null) {
        return {type: 'empty-set-version'};
      }
      const selectedMemoryPage = setVersion.pages.find((memoryPage) => memoryPage.page === selectedPage);
      return {type: 'page', memoryPage: selectedMemoryPage ?? firstPage};
    }
    default:
      return assertNever(setVersionResult);
  }
}

export function MemoryViewerPanel({
  agentName,
  initialPage,
  initialVersionId,
  onClose,
  routineId,
  totalSetVersionCount,
  versions,
  versionsTruncated,
}: MemoryViewerPanelProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [selectedPage, setSelectedPage] = useState<string | null>(initialPage);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(initialVersionId);
  const [filter, setFilter] = useState('');
  const effectiveVersionId =
    selectedVersionId != null &&
    // A deep-linked set version may predate the loaded history window; trust it since it is fetched by id.
    (selectedVersionId === initialVersionId ||
      versions.some((setVersion) => setVersion.versionId === selectedVersionId))
      ? selectedVersionId
      : (versions[0]?.versionId ?? null);
  const setVersionResult = useSkippableBenchlingQuery<
    RoutineMemorySetVersionQuery,
    RoutineMemorySetVersionQueryVariables
  >(ROUTINE_MEMORY_SET_VERSION_QUERY, {
    variables: {routineId, versionId: effectiveVersionId ?? ''},
    skip: effectiveVersionId == null,
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });
  const pages = useMemo(
    () =>
      setVersionResult.type === QueryResultType.SUCCESS &&
      setVersionResult.data.routineMemorySetVersion?.versionId === effectiveVersionId
        ? setVersionResult.data.routineMemorySetVersion.pages
        : [],
    [effectiveVersionId, setVersionResult]
  );
  const setVersionHistoryItems = useMemo(
    () =>
      versions.map((setVersion, index) => ({
        ...setVersion,
        changeSummary: summarizeSetVersionChanges(
          setVersion.members,
          versions[index + 1]?.members ?? (versionsTruncated && index === versions.length - 1 ? null : [])
        ),
        isLatest: index === 0,
      })),
    [versions, versionsTruncated]
  );
  const isViewingLatestSetVersion = effectiveVersionId === setVersionHistoryItems[0]?.versionId;
  const isViewingOlderSetVersion = !isViewingLatestSetVersion && effectiveVersionId != null;
  const filteredPages = useMemo(() => {
    const normalizedFilter = filter.toLowerCase();
    return pages.filter((memoryPage) => memoryPage.page.toLowerCase().includes(normalizedFilter));
  }, [filter, pages]);
  const loadedCount = pages.length;
  const visiblePageCount = filteredPages.length;
  const isFilterActive = filter.trim().length > 0;
  const contentState = useMemo(
    () => getMemoryContentState(setVersionResult, effectiveVersionId, selectedPage),
    [effectiveVersionId, selectedPage, setVersionResult]
  );
  const effectivePage = contentState.type === 'page' ? contentState.memoryPage.page : null;

  const handleSelectPage = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const page = event.currentTarget.dataset.page;
    if (page != null) {
      setSelectedPage(page);
    }
  }, []);

  const handleFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.currentTarget.value);
  }, []);

  const handleSetVersionMenuItemClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const versionId = event.currentTarget.dataset.versionId;
    if (versionId != null) {
      setSelectedVersionId(versionId);
      setSelectedPage(null);
      setFilter('');
    }
  }, []);

  const handleReturnToLatest = useCallback(() => {
    const latestVersionId = setVersionHistoryItems[0]?.versionId;
    if (latestVersionId != null) {
      setSelectedVersionId(latestVersionId);
      setSelectedPage(null);
      setFilter('');
    }
  }, [setVersionHistoryItems]);

  let content: JSX.Element;
  switch (contentState.type) {
    case 'loading':
      content = (
        <Center role="status">
          <Stack align="center" spacing="xs">
            <Loader size="sm" />
            <Text size="sm" color="dimmed">
              Loading memory...
            </Text>
          </Stack>
        </Center>
      );
      break;
    case 'error':
      content = (
        <Center role="alert">
          <Text size="sm">Unable to load this agent's memory.</Text>
        </Center>
      );
      break;
    case 'unavailable':
      content = (
        <Center role="status">
          <Text size="sm" color="dimmed">
            This memory set version is no longer available.
          </Text>
        </Center>
      );
      break;
    case 'empty-set-version':
      content = (
        <Center>
          <Text size="sm" color="dimmed">
            This set version contains no memory files.
          </Text>
        </Center>
      );
      break;
    case 'page':
      content = (
        <Stack spacing="md">
          <Text component="h5" className={classes.contentPath} fw={600}>
            {contentState.memoryPage.page}
          </Text>
          <Divider />
          <LLMMarkdownSection markdown={contentState.memoryPage.content} />
        </Stack>
      );
      break;
    default:
      content = assertNever(contentState);
  }

  const setVersionHistoryMenu =
    setVersionHistoryItems.length > 0 ? (
      <Menu
        key="set-version-history-menu"
        position="bottom-end"
        shadow="md"
        width={440}
        withinPortal={true}
        zIndex={ZINDEX_FLOATING_UI}
      >
        <Menu.Target>
          <Button
            leftIcon={<BenchlingIcon aria-hidden={true} icon="history" />}
            rightIcon={<BenchlingIcon aria-hidden={true} icon="chevron-down" />}
            size="sm"
            variant="ghost"
          >
            {isViewingOlderSetVersion ? 'Viewing history' : 'View history'}
          </Button>
        </Menu.Target>
        <Menu.Dropdown className={classes.setVersionMenuDropdown}>
          <Menu.Label>{totalSetVersionCount} total</Menu.Label>
          {setVersionHistoryItems.map((setVersion) => {
            const isSelected = setVersion.versionId === effectiveVersionId;
            return (
              <Menu.Item
                key={setVersion.versionId}
                aria-label={`${
                  setVersion.isLatest ? 'Current memory version' : 'Memory version'
                }, ${readableTime(setVersion.createdAt)}; ${setVersion.changeSummary}`}
                aria-current={isSelected}
                className={cx(classes.setVersionMenuItem, isSelected && classes.setVersionMenuItemSelected)}
                data-version-id={setVersion.versionId}
                onClick={handleSetVersionMenuItemClick}
                rightSection={isSelected ? <BenchlingIcon aria-hidden={true} icon="check" /> : undefined}
              >
                <Stack spacing={4}>
                  <Group position="apart" noWrap={true} spacing="xs">
                    <Text
                      aria-label="Set version time"
                      color="dimmed"
                      component="time"
                      dateTime={setVersion.createdAt}
                      size="xs"
                    >
                      {readableTime(setVersion.createdAt)}
                    </Text>
                    {setVersion.isLatest && (
                      <Badge size="xs" variant="light">
                        Current
                      </Badge>
                    )}
                  </Group>
                  <Text aria-label="Change summary" className={classes.setVersionMenuSummary} size="sm">
                    {setVersion.changeSummary}
                  </Text>
                </Stack>
              </Menu.Item>
            );
          })}
          {versionsTruncated && (
            <>
              <Menu.Divider />
              <Text className={classes.setVersionMenuNote} color="dimmed" size="xs">
                Only the latest {versions.length} are available.
              </Text>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    ) : null;

  return (
    <div aria-labelledby={MEMORY_VIEWER_PANEL_TITLE_ID} className={classes.panel} role="region">
      <Stack className={classes.header} spacing="md">
        <Group spacing="xs">
          <ActionIcon
            aria-label={`Back to ${agentName}`}
            autoFocus={true}
            onClick={onClose}
            size="sm"
            variant="ghost"
          >
            <BenchlingIcon icon="arrow-left" />
          </ActionIcon>
          <Text size="lg" fw={600}>
            {agentName}
          </Text>
        </Group>
        <Group align="center" position="apart">
          <Text component="h4" fw={600} id={MEMORY_VIEWER_PANEL_TITLE_ID} size="xl">
            Memory
          </Text>
          {setVersionHistoryMenu != null && (
            <Group noWrap={true} spacing="xs">
              {isViewingOlderSetVersion && (
                <Button key="return-to-latest" onClick={handleReturnToLatest} size="sm" variant="ghost">
                  Return to latest
                </Button>
              )}
              {setVersionHistoryMenu}
            </Group>
          )}
        </Group>
      </Stack>
      <Divider />
      <Box className={classes.body}>
        {versions.length > 0 && (
          <Box component="nav" aria-label="Memory files" className={classes.pageList}>
            {pages.length > 0 && (
              <Stack spacing="md">
                <Stack className={classes.pageListControls} spacing="xs">
                  <Text color="dimmed" size="xs">
                    {isFilterActive
                      ? `showing ${visiblePageCount} of ${loadedCount}`
                      : `${loadedCount} ${loadedCount === 1 ? 'file' : 'files'}`}
                  </Text>
                  <TextInput
                    aria-label="Filter memory files"
                    icon={<BenchlingIcon icon="search" />}
                    onChange={handleFilterChange}
                    placeholder="Filter memory"
                    size="xs"
                    value={filter}
                  />
                </Stack>
                {filteredPages.length === 0 ? (
                  <Text color="dimmed" size="xs">
                    No matching files
                  </Text>
                ) : (
                  <Stack spacing={4}>
                    {filteredPages.map((memoryPage) => (
                      <Button
                        key={memoryPage.page}
                        aria-label={memoryPage.page}
                        aria-pressed={effectivePage === memoryPage.page}
                        className={classes.pageButton}
                        classNames={{inner: classes.pageButtonInner, label: classes.pageButtonLabel}}
                        color={effectivePage === memoryPage.page ? 'blue' : 'gray'}
                        data-page={memoryPage.page}
                        onClick={handleSelectPage}
                        size="sm"
                        title={memoryPage.page}
                        variant={effectivePage === memoryPage.page ? 'light' : 'subtle'}
                      >
                        {memoryPage.page}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
        )}
        <Box component="section" aria-label={effectivePage ?? 'Memory content'} className={classes.content}>
          {content}
        </Box>
      </Box>
    </div>
  );
}
